import { CircularProgress, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import resendConfirmationCode from '../actions/resendConfirmationCode';
import api from '../api/api';
import ErrorCodes from '../consts/ErrorCodes';
import useStore from '../store/store';

const ConfirmSchema = Yup.object().shape({
    code: Yup.string().required('Required'),
});

const setErrorMessage = (errCode) => {
    switch (errCode) {
        case ErrorCodes.already_confirmed:
            return 'Password change already confirmed!';
        case ErrorCodes.invalid_param:
            return 'Invalid parameter!';
        case ErrorCodes.invalid_token:
            return 'Invalid token!';
        case ErrorCodes.access_denied:
            return 'The code is invalid or has expired!';
        case ErrorCodes.too_many_attempts:
            return 'Too many attempts!';
        default:
            return 'Incorrect SMS code!';
    }
};

export default function ConfirmPasswordChangePage() {
    const formError = useStore((state) => state.formError);
    const code = '';
    const loading = useStore((state) => state.loading);
    const setFormError = useStore((state) => state.setFormError);
    const setLoading = useStore((state) => state.setLoading);
    const [confirmed, setConfirmed] = useState(false);
    const location = useLocation();
    const iso2 = location.state?.iso2 || '';
    const phone = location.state?.phone || '';

    const confirm = async ({ code }) => {
        setLoading(true);
        try {
            const response = await api.post('/security/confirm', {
                iso2,
                phone,
                code,
            });

            const { success, errCode } = response.data;
            if (success) {
                setLoading(false);
                setConfirmed(true);
            } else if (!success && errCode) {
                setLoading(false);
                setFormError(errCode);
            }
        } catch (e) {
            console.error(e);
            if (e.code) setFormError(e.code);
        }
    };

    const classes = useStyles();

    if (confirmed) {
        return <Navigate to="/login" />;
    }

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <div className={classes.paper}>
                <Typography component="h1" variant="h5">
                    Confirm password change
                </Typography>
                <Formik
                    className={classes.form}
                    initialValues={{
                        code: code,
                    }}
                    onSubmit={confirm}
                    validationSchema={ConfirmSchema}
                    validateOnBlur
                    validateOnChange
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        resetForm,
                    }) => (
                        <Form>
                            <Field
                                component={TextField}
                                value={values.code}
                                disabled={loading}
                                error={errors.code && touched.code}
                                fullWidth
                                helperText={
                                    errors.code && touched.code
                                        ? errors.code
                                        : ''
                                }
                                id="code"
                                label="SMS code"
                                margin="normal"
                                name="code"
                                onBlur={handleBlur}
                                onChange={(value) => {
                                    formError && setFormError(null);
                                    handleChange(value);
                                }}
                                size="small"
                                type="text"
                                variant="outlined"
                            />
                            {formError && (
                                <Typography
                                    align="center"
                                    className={classes.errorMessage}
                                    color="error"
                                    variant="body1"
                                >
                                    {setErrorMessage(formError)}
                                </Typography>
                            )}
                            <div className={classes.wrapper}>
                                <Button
                                    color="primary"
                                    disabled={loading}
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                >
                                    Confirm
                                </Button>
                                {loading && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </div>
                            <Link
                                onClick={() => {
                                    formError && setFormError(null);
                                    resetForm();
                                    resendConfirmationCode(iso2, phone);
                                }}
                            >
                                <Typography
                                    align="center"
                                    variant="body1"
                                    className={classes.notification}
                                >
                                    Resend SMS
                                </Typography>
                            </Link>
                            <Link to="/login">
                                <Typography
                                    align="center"
                                    variant="body1"
                                    className={classes.notification}
                                >
                                    Cancel
                                </Typography>
                            </Link>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
}

const useStyles = makeStyles((theme) => ({
    buttonProgress: {
        color: 'primary',
        left: '50%',
        marginLeft: -12,
        marginTop: -12,
        position: 'absolute',
        top: '50%',
    },
    errorMessage: {
        marginTop: theme.spacing(2),
    },
    form: {
        marginTop: theme.spacing(1),
        width: '100%', // Fix IE 11 issue.
    },
    paper: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(8),
    },
    wrapper: {
        margin: theme.spacing(3, 0, 2),
        position: 'relative',
    },
    notification: {
        color: '#7C87FF',
        fontWeight: 'bold',
    },
}));
